export default class ChatWidget {
    constructor(element) {
        this.element = element;
        this.bodyEl = element.querySelector('.chat-widget__body');
        this.messagesScrollEl = element.querySelector('.chat-widget__messages-scroll');
        this.messagesEl = element.querySelector('.chat-widget__messages');
        this.controlsEl = element.querySelector('.chat-widget__controls');
        this.progressWrapperEl = element.querySelector('.chat-widget__progress');
        this.progressLabelEl = element.querySelector('[data-chat-progress-label]');
        this.progressTrackEl = element.querySelector('[data-chat-progress-track]');
        this.progressFillEl = element.querySelector('[data-chat-progress-fill]');
        this.flowSlug = element.dataset.flowSlug;
        this.flowEndpoint = element.dataset.flowEndpoint;
        this.leadEndpoint = element.dataset.leadEndpoint;
        this.csrfToken = element.dataset.csrf;

        this.flow = null;
        this.blocks = [];
        this.currentIndex = 0;
        this.answers = {};
        this.uploadFilesByBlock = {};
        this.leadFormRendered = false;
        this.isSubmittingLead = false;
        this.progressBlocks = [];
        this.answeredBlockIds = new Set();
        this.scrollContainerEl = null;
        this.scrollListener = null;
        this.lastRenderedChapterId = null;
        this.flowAborted = false;
        this.showChapterDividers = true;
        this.packageModalEl = null;
        this.packageModalTitleEl = null;
        this.packageModalItemsEl = null;
        this.packageModalCloseEl = null;
        this.packageModalKeyHandler = null;
        this.packageModalTitleId = `chat-package-modal-title-${Math.random().toString(36).slice(2, 10)}`;
        this.packageModalPackageTabsEl = null;
        this.packageModalComponentTabsEl = null;
        this.packageModalContentEl = null;
        this.packageModalState = {
            packages: [],
            activePackageId: null,
            groups: [],
            activeGroupKey: null,
        };
        this.chatSessionStartedAt = Date.now();
        this.activeTrackingContext = null;
        this.chatStartedTracked = false;
        this.chatCompletedTracked = false;
        this.chatAbortedTracked = false;
        this.chatDropoffTracked = false;
        this.lifecycleTrackingBound = false;
        this.boundPageHideHandler = null;
        this.boundBeforeUnloadHandler = null;
        this.boundVisibilityChangeHandler = null;

        this.init();
    }

    async init() {
        try {
            const response = await fetch(this.flowEndpoint, { headers: { Accept: 'application/json' } });
            if (!response.ok) {
                throw new Error('Flow konnte nicht geladen werden.');
            }
            this.flow = await response.json();
            this.applyFlowSettings();
            this.blocks = this.flattenBlocks(this.flow);
            this.blocks = this.injectEntryTimeCheckIfNeeded(this.blocks);
            this.progressBlocks = this.blocks.filter((block) => this.isAnswerableBlock(block));
            this.updateProgress();
            if (this.blocks.length === 0) {
                this.addBotBubble('Aktuell ist kein aktiver Flow verfügbar.');
                return;
            }
            this.bindLifecycleTracking();
            await this.showBlock(this.blocks[0]);
        } catch (error) {
            this.addBotBubble('Der Chat konnte nicht geladen werden.');
        }
    }

    applyFlowSettings() {
        const settings = this.flow?.settings || {};

        const showProgressbar = this.normalizeBoolean(settings.show_progressbar, true);
        if (this.progressWrapperEl) {
            this.progressWrapperEl.hidden = !showProgressbar;
        }

        this.showChapterDividers = this.normalizeBoolean(settings.show_chapter_dividers, true);

        const primaryColor = this.normalizeHexColor(settings.primary_color);
        if (primaryColor) {
            this.element.style.setProperty('--chat-primary', primaryColor);
            this.element.style.setProperty('--chat-primary-hover', this.shiftColor(primaryColor, -18));
            this.element.style.setProperty('--chat-primary-contrast', this.contrastColor(primaryColor));
        }

        const secondaryColor = this.normalizeHexColor(settings.secondary_color);
        if (secondaryColor) {
            this.element.style.setProperty('--chat-secondary', secondaryColor);
        }

        const dangerColor = this.normalizeHexColor(settings.danger_color);
        if (dangerColor) {
            this.element.style.setProperty('--chat-danger', dangerColor);
            this.element.style.setProperty('--chat-danger-hover', this.shiftColor(dangerColor, -18));
            this.element.style.setProperty('--chat-danger-contrast', this.contrastColor(dangerColor));
        }

        const userBubbleColor = this.normalizeHexColor(settings.user_bubble_color);
        if (userBubbleColor) {
            this.element.style.setProperty('--chat-bubble-right', userBubbleColor);
            this.element.style.setProperty('--chat-bubble-right-text', this.contrastColor(userBubbleColor));
        }

        const maxChatHeight = this.normalizePositiveInt(settings.max_chat_height);
        const scrollContainer = this.element;
        if (!scrollContainer) {
            return;
        }

        if (maxChatHeight) {
            this.element.style.setProperty('--chat-max-height', `${maxChatHeight}px`);
            this.element.classList.add('chat-widget__scroll-capped');
            this.bodyEl?.classList.remove('chat-widget__scroll-capped');
            this.bindScrollFade(scrollContainer);
            this.updateTopFadeState(scrollContainer);
        } else {
            this.element.style.removeProperty('--chat-max-height');
            this.element.classList.remove('chat-widget__scroll-capped');
            this.bodyEl?.classList.remove('chat-widget__scroll-capped');
            scrollContainer.classList.remove('chat-widget__is-scrolled');
            this.unbindScrollFade();
        }
    }

    flattenBlocks(flow) {
        const blocks = [];
        (flow.chapters || []).forEach((chapter) => {
            (chapter.blocks || []).forEach((block) => {
                blocks.push({
                    ...block,
                    chapter_id: chapter.id,
                    chapter_title: chapter.title,
                });
            });
        });
        return blocks;
    }

    async showBlock(block) {
        if (!block) {
            return;
        }

        this.updateTrackingContext(block);
        this.trackChatStart();

        if (this.showChapterDividers && block.chapter_id && block.chapter_id !== this.lastRenderedChapterId) {
            this.addChapterDivider(block.chapter_title || 'Abschnitt');
            this.lastRenderedChapterId = block.chapter_id;
        }

        this.updateProgress(block);
        await this.showTyping();
        this.addBotBubble(block.question || '');

        if (block.helper_text) {
            this.addBotHelper(block.helper_text);
        }

        this.renderControls(block);
    }

    renderControls(block) {
        this.clearControls();

        switch (block.type) {
            case 'message':
                this.next();
                return;
            case 'summary':
                this.renderSummary(block);
                this.next();
                return;
            case 'yes_no':
                this.renderYesNo(block);
                return;
            case 'single_choice':
                this.renderSingleChoice(block);
                return;
            case 'multi_choice':
                this.renderMultiChoice(block);
                return;
            case 'rating':
                this.renderRating(block);
                return;
            case 'input':
                this.renderInput(block);
                return;
            case 'file_upload':
                this.renderFileUpload(block);
                return;
            case 'package_select':
                this.renderPackageSelect(block);
                return;
            case 'addon_select':
                this.renderAddonSelect(block);
                return;
            default:
                this.next();
        }
    }

    renderYesNo(block) {
        const yesLabel = block.config?.yes_label || 'Ja';
        const noLabel = block.config?.no_label || 'Nein';

        const wrapper = this.createControlsContainer();
        wrapper.appendChild(this.createButton(yesLabel, () => this.handleAnswer(block, 'yes', yesLabel)));
        wrapper.appendChild(this.createButton(noLabel, () => this.handleAnswer(block, 'no', noLabel), 'chat-button--danger'));
        this.controlsEl.appendChild(wrapper);
    }

    renderSingleChoice(block) {
        const list = document.createElement('div');
        list.className = 'chat-choice';

        (block.options || []).forEach((option) => {
            const button = this.createChoiceButton(option, () => {
                this.handleAnswer(block, option.value, option.label);
            });
            list.appendChild(button);
        });

        this.controlsEl.appendChild(list);
    }

    renderMultiChoice(block) {
        const list = document.createElement('div');
        list.className = 'chat-choice';

        const selected = new Set();
        const maxChoices = parseInt(block.config?.max_choices || 0, 10);
        const error = document.createElement('div');
        error.className = 'chat-error';
        const submitButton = this.createButton('Auswahl senden', () => {
            const values = Array.from(selected.values());
            if (values.length === 0) {
                error.textContent = 'Bitte wählen Sie mindestens eine Option aus.';
                return;
            }
            const labels = values.map((value) => {
                const option = (block.options || []).find((opt) => opt.value === value);
                return option?.label || value;
            });
            error.textContent = '';
            this.handleAnswer(block, values, labels.join(', '));
        });
        submitButton.disabled = true;

        (block.options || []).forEach((option) => {
            const button = this.createChoiceButton(option, () => {
                if (selected.has(option.value)) {
                    selected.delete(option.value);
                    button.classList.remove('is-active');
                } else {
                    if (maxChoices && selected.size >= maxChoices) {
                        error.textContent = `Maximal ${maxChoices} Auswahl${maxChoices > 1 ? 'en' : ''} möglich.`;
                        return;
                    }
                    selected.add(option.value);
                    button.classList.add('is-active');
                }
                error.textContent = '';
                submitButton.disabled = selected.size === 0;
            });
            list.appendChild(button);
        });

        this.controlsEl.appendChild(list);
        this.controlsEl.appendChild(error);
        this.controlsEl.appendChild(this.wrapControls(submitButton));
    }

    renderRating(block) {
        const min = parseInt(block.config?.min || 1, 10);
        const max = parseInt(block.config?.max || 5, 10);
        const wrapper = document.createElement('div');
        wrapper.className = 'chat-rating';

        let selectedValue = null;
        for (let value = min; value <= max; value += 1) {
            const star = document.createElement('button');
            star.type = 'button';
            star.className = 'chat-rating__star';
            star.innerHTML = this.starIcon();
            star.addEventListener('click', () => {
                selectedValue = value;
                this.updateStars(wrapper, value);
                submitButton.disabled = false;
            });
            wrapper.appendChild(star);
        }

        const submitButton = this.createButton('Weiter', () => {
            this.handleAnswer(block, selectedValue, `${selectedValue} / ${max}`);
        });
        submitButton.disabled = true;

        this.controlsEl.appendChild(wrapper);
        this.controlsEl.appendChild(this.wrapControls(submitButton));
    }

    renderInput(block) {
        const inputType = block.config?.input_type || 'text';
        const input = inputType === 'textarea' ? document.createElement('textarea') : document.createElement('input');
        input.className = 'chat-input';
        if (inputType !== 'textarea') {
            input.type = inputType;
        }
        if (block.placeholder) {
            input.placeholder = block.placeholder;
        }

        const error = document.createElement('div');
        error.className = 'chat-error';

        const submitButton = this.createButton('Weiter', () => {
            const value = input.value.trim();
            if (block.is_required && !value) {
                error.textContent = 'Bitte füllen Sie das Feld aus.';
                return;
            }
            error.textContent = '';
            if (!value) {
                this.next();
                return;
            }
            this.handleAnswer(block, value, value);
        });

        this.controlsEl.appendChild(input);
        this.controlsEl.appendChild(error);
        this.controlsEl.appendChild(this.wrapControls(submitButton));
    }

    renderFileUpload(block) {
        const config = block.config || {};
        const maxFiles = this.normalizePositiveInt(config.max_files) || 5;
        const maxFileSizeMb = this.normalizePositiveInt(config.max_file_size_mb) || 15;
        const maxFileSizeBytes = maxFileSizeMb * 1024 * 1024;
        const acceptedMimeTypes = this.normalizeUploadMimeTypes(config.accepted_mime_types);
        const skipLabel = this.normalizeString(config.skip_label, 'Nein - weiter');

        const container = document.createElement('div');
        container.className = 'chat-file-upload';

        const input = document.createElement('input');
        input.type = 'file';
        input.className = 'chat-input chat-file-upload__input';
        input.multiple = maxFiles !== 1;
        input.accept = this.toFileInputAccept(acceptedMimeTypes);
        container.appendChild(input);

        const hint = document.createElement('p');
        hint.className = 'chat-file-upload__hint';
        hint.textContent = `Erlaubt: ${acceptedMimeTypes.join(', ')} · Max. ${maxFiles} Datei${maxFiles > 1 ? 'en' : ''} · je max. ${maxFileSizeMb} MB`;
        container.appendChild(hint);

        const selectedList = document.createElement('div');
        selectedList.className = 'chat-file-upload__list';
        container.appendChild(selectedList);

        const error = document.createElement('div');
        error.className = 'chat-error';

        const updateSelectedList = () => {
            selectedList.innerHTML = '';
            Array.from(input.files || []).forEach((file) => {
                const row = document.createElement('div');
                row.className = 'chat-file-upload__item';
                row.textContent = `${file.name} (${this.formatFileSize(file.size)})`;
                selectedList.appendChild(row);
            });
        };

        input.addEventListener('change', () => {
            error.textContent = '';
            updateSelectedList();
        });

        const submitButton = this.createButton('Dateien übernehmen', () => {
            const files = Array.from(input.files || []);
            if (files.length === 0) {
                if (block.is_required) {
                    error.textContent = 'Bitte laden Sie mindestens eine Datei hoch.';
                    return;
                }

                error.textContent = `Bitte laden Sie Dateien hoch oder wählen Sie "${skipLabel}".`;
                return;
            }

            if (files.length > maxFiles) {
                error.textContent = `Bitte laden Sie maximal ${maxFiles} Dateien hoch.`;
                return;
            }

            for (const file of files) {
                if (!this.isAllowedUploadMimeType(file, acceptedMimeTypes)) {
                    error.textContent = `Ungültiger Dateityp für "${file.name}".`;
                    return;
                }

                if ((file.size || 0) > maxFileSizeBytes) {
                    error.textContent = `Die Datei "${file.name}" ist größer als ${maxFileSizeMb} MB.`;
                    return;
                }
            }

            this.uploadFilesByBlock[String(block.id)] = files;
            const values = files.map((file) => ({
                name: file.name,
                mime: file.type || null,
                size: file.size || 0,
            }));
            this.handleAnswer(block, values, files.map((file) => file.name).join(', '));
        });

        const controls = this.createControlsContainer();
        controls.appendChild(submitButton);

        if (!block.is_required) {
            const skipButton = this.createButton(skipLabel, () => {
                error.textContent = '';
                input.value = '';
                updateSelectedList();
                this.uploadFilesByBlock[String(block.id)] = [];
                this.answeredBlockIds.add(block.id);
                this.addUserBubble(skipLabel);
                this.clearControls();
                this.next();
            }, 'chat-button--danger');
            controls.appendChild(skipButton);
        }

        this.controlsEl.appendChild(container);
        this.controlsEl.appendChild(error);
        this.controlsEl.appendChild(controls);
    }

    renderSummary() {
        const summary = this.buildSummary();
        const container = document.createElement('div');
        container.className = 'chat-bubble chat-bubble--left chat-summary';

        summary.forEach((section) => {
            const title = document.createElement('div');
            title.className = 'chat-summary__chapter';
            title.textContent = section.chapterTitle;
            container.appendChild(title);

            section.items.forEach((item) => {
                const row = document.createElement('div');
                row.className = 'chat-summary__item';
                row.innerHTML = `<span class="chat-summary__label">${item.question}</span><span>${item.answer}</span>`;
                container.appendChild(row);
            });
        });

        this.messagesEl.appendChild(container);
    }

    renderLeadForm() {
        this.leadFormRendered = true;
        const form = document.createElement('div');
        form.className = 'chat-lead-form';

        const nameRow = document.createElement('div');
        nameRow.className = 'chat-lead-form__row';

        const firstName = document.createElement('input');
        firstName.type = 'text';
        firstName.className = 'chat-input';
        firstName.placeholder = 'Vorname';
        firstName.required = true;

        const lastName = document.createElement('input');
        lastName.type = 'text';
        lastName.className = 'chat-input';
        lastName.placeholder = 'Nachname';
        lastName.required = true;

        nameRow.appendChild(firstName);
        nameRow.appendChild(lastName);

        const email = document.createElement('input');
        email.type = 'email';
        email.className = 'chat-input';
        email.placeholder = 'E-Mail-Adresse';

        const phone = document.createElement('input');
        phone.type = 'tel';
        phone.className = 'chat-input';
        phone.placeholder = 'Telefon (optional)';

        const consentWrapper = document.createElement('label');
        consentWrapper.className = 'chat-consent';
        const consent = document.createElement('input');
        consent.type = 'checkbox';

        const consentLabel = this.normalizeConsentText(
            this.flow?.settings?.consent_label,
            'Ich stimme der Datenschutzerklärung zu.',
        );
        const privacyPolicyUrl = this.normalizeString(this.flow?.settings?.privacy_policy_url, '/datenschutz');
        const privacyPolicyLabel = this.normalizePrivacyPolicyLabel(
            this.flow?.settings?.privacy_policy_label,
            'Datenschutzerklärung',
        );

        const consentText = document.createElement('span');
        consentText.className = 'chat-consent__text';

        // Prefer linking the actual "Datenschutz"/"Datenschutzerklärung" term in the consent text.
        const privacyWordPattern = /\bDatenschutz(?:erklärung)?\b/i;
        const privacyWordMatch = consentLabel.match(privacyWordPattern);

        const escapedPrivacyPolicyLabel = privacyPolicyLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const privacyPolicyPattern = new RegExp(escapedPrivacyPolicyLabel, 'i');
        const labelMatch = privacyWordMatch || consentLabel.match(privacyPolicyPattern);

        const privacyPolicyLink = document.createElement('a');
        privacyPolicyLink.className = 'chat-consent__link';
        privacyPolicyLink.href = privacyPolicyUrl;
        privacyPolicyLink.target = '_blank';
        privacyPolicyLink.rel = 'noopener noreferrer';
        privacyPolicyLink.textContent = labelMatch?.[0] || privacyPolicyLabel;

        if (labelMatch && typeof labelMatch.index === 'number') {
            const prefix = consentLabel.slice(0, labelMatch.index);
            const suffix = consentLabel.slice(labelMatch.index + labelMatch[0].length);
            consentText.appendChild(document.createTextNode(` ${prefix}`));
            consentText.appendChild(privacyPolicyLink);
            consentText.appendChild(document.createTextNode(suffix));
        } else {
            consentText.appendChild(document.createTextNode(` ${consentLabel} `));
            consentText.appendChild(privacyPolicyLink);
        }

        consentWrapper.appendChild(consent);
        consentWrapper.appendChild(consentText);

        const newsletterWrapper = document.createElement('label');
        newsletterWrapper.className = 'chat-consent chat-consent--optional';
        const newsletter = document.createElement('input');
        newsletter.type = 'checkbox';
        newsletterWrapper.appendChild(newsletter);
        newsletterWrapper.appendChild(document.createTextNode(' Newsletteranmeldung (optional, jederzeit widerrufbar)'));

        const consentNote = document.createElement('p');
        consentNote.className = 'chat-consent-note';
        consentNote.textContent = 'Hinweis: Einwilligungen können jederzeit mit Wirkung für die Zukunft widerrufen werden.';

        const error = document.createElement('div');
        error.className = 'chat-error';

        const leadSubmitLabel = this.flow?.settings?.lead_submit_label || 'Anfrage absenden';
        const submitButton = this.createButton(leadSubmitLabel, async () => {
            if (this.isSubmittingLead) {
                return;
            }

            error.textContent = '';
            if (!firstName.value.trim()) {
                error.textContent = 'Bitte geben Sie Ihren Vornamen an.';
                return;
            }
            if (!lastName.value.trim()) {
                error.textContent = 'Bitte geben Sie Ihren Nachnamen an.';
                return;
            }
            if (!email.value.trim()) {
                error.textContent = 'Bitte geben Sie Ihre E-Mail-Adresse an.';
                return;
            }
            if (!this.isValidEmail(email.value.trim())) {
                error.textContent = 'Bitte geben Sie eine gültige E-Mail-Adresse an.';
                return;
            }
            if (!consent.checked) {
                error.textContent = 'Bitte bestätigen Sie die Datenschutzerklärung.';
                return;
            }

            this.isSubmittingLead = true;
            this.setButtonLoading(submitButton, true);

            const wasSuccessful = await this.submitLead({
                first_name: firstName.value.trim() || null,
                last_name: lastName.value.trim() || null,
                email: email.value.trim(),
                phone: phone.value.trim(),
                consent: true,
                newsletter_opt_in: newsletter.checked,
                answers: this.buildAnswerPayload(),
            });

            this.isSubmittingLead = false;
            if (!wasSuccessful) {
                this.setButtonLoading(submitButton, false, leadSubmitLabel);
            }
        });

        form.appendChild(nameRow);
        form.appendChild(email);
        form.appendChild(phone);
        form.appendChild(consentWrapper);
        form.appendChild(newsletterWrapper);
        form.appendChild(consentNote);
        form.appendChild(error);
        form.appendChild(this.wrapControls(submitButton));

        this.controlsEl.appendChild(form);
    }

    renderPackageSelect(block) {
        const packages = Array.isArray(block.packages) ? block.packages : [];
        if (packages.length === 0) {
            this.addBotHelper('Aktuell sind keine Pakete verfügbar.');
            this.next();
            return;
        }

        const list = document.createElement('div');
        list.className = 'chat-package-list';
        const error = document.createElement('div');
        error.className = 'chat-error';
        let selectedPackage = null;
        const cards = [];

        const submitButton = this.createButton('Paket übernehmen', () => {
            if (!selectedPackage) {
                error.textContent = 'Bitte wählen Sie ein Paket aus.';
                return;
            }

            error.textContent = '';
            const priceLabel = this.formatPricingRange(selectedPackage.pricing);
            const display = priceLabel ? `${selectedPackage.name} (${priceLabel})` : selectedPackage.name;
            this.handleAnswer(block, selectedPackage.id, display);
        });
        submitButton.disabled = true;

        packages.forEach((pkg) => {
            const card = this.createPackageCard(pkg, packages, () => {
                selectedPackage = pkg;
                cards.forEach((node) => node.classList.remove('is-active'));
                card.classList.add('is-active');
                error.textContent = '';
                submitButton.disabled = false;
            });

            const cardWrap = document.createElement('div');
            cardWrap.className = 'chat-package-card-wrap';
            cardWrap.appendChild(card);

            cards.push(card);
            list.appendChild(cardWrap);
        });

        this.controlsEl.appendChild(list);
        this.controlsEl.appendChild(error);
        this.controlsEl.appendChild(this.wrapControls(submitButton));
    }

    renderAddonSelect(block) {
        const addons = Array.isArray(block.addons) ? block.addons : [];
        if (addons.length === 0) {
            this.addBotHelper('Für dieses Paket sind aktuell keine Add-ons verfügbar.');
            this.next();
            return;
        }

        const selected = new Set();
        const maxChoices = parseInt(block.config?.max_choices || 0, 10);
        const selectedPackageId = this.getSelectedPackageId();
        const list = document.createElement('div');
        list.className = 'chat-package-list chat-package-list--addons';
        const error = document.createElement('div');
        error.className = 'chat-error';

        const submitButton = this.createButton('Add-ons übernehmen', () => {
            const values = Array.from(selected.values());
            const labels = addons
                .filter((addon) => values.includes(addon.id))
                .map((addon) => addon.name);

            if (block.is_required && values.length === 0) {
                error.textContent = 'Bitte wählen Sie mindestens ein Add-on aus.';
                return;
            }

            error.textContent = '';
            if (values.length === 0) {
                this.next();
                return;
            }

            this.handleAnswer(block, values, labels.join(', '));
        });

        addons.forEach((addon) => {
            const allowed = this.isAddonAllowedByConstraints(addon, selectedPackageId, selected);
            const card = this.createAddonCard(addon, allowed, () => {
                if (!this.isAddonAllowedByConstraints(addon, selectedPackageId, selected)) {
                    error.textContent = 'Dieses Add-on ist mit der aktuellen Auswahl nicht kombinierbar.';
                    return;
                }

                if (selected.has(addon.id)) {
                    selected.delete(addon.id);
                    card.classList.remove('is-active');
                } else {
                    if (maxChoices && selected.size >= maxChoices) {
                        error.textContent = `Maximal ${maxChoices} Add-on${maxChoices > 1 ? 's' : ''} möglich.`;
                        return;
                    }
                    selected.add(addon.id);
                    card.classList.add('is-active');
                }

                error.textContent = '';
            });

            list.appendChild(card);
        });

        this.controlsEl.appendChild(list);
        this.controlsEl.appendChild(error);
        this.controlsEl.appendChild(this.wrapControls(submitButton));
    }

    async submitLead(payload) {
        try {
            const hasUploadFiles = this.hasUploadFiles();
            const headers = {
                'X-CSRF-TOKEN': this.csrfToken,
                Accept: 'application/json',
            };

            const requestOptions = {
                method: 'POST',
                headers,
            };

            if (hasUploadFiles) {
                requestOptions.body = this.buildLeadFormData(payload);
            } else {
                requestOptions.headers['Content-Type'] = 'application/json';
                requestOptions.body = JSON.stringify(payload);
            }

            const response = await fetch(this.leadEndpoint, requestOptions);

            if (!response.ok) {
                const errorMessage = await this.extractErrorMessage(response);
                throw new Error(errorMessage);
            }

            const responsePayload = await this.extractSuccessPayload(response);

            this.setProgressComplete();
            this.trackChatCompletion();
            this.unbindLifecycleTracking();
            const successMessage = this.flow?.settings?.success_message
                || 'Vielen Dank! Wir haben Ihre Anfrage erhalten.';
            this.addBotBubble(successMessage);
            const ctaUrl = this.buildLeadCtaUrl(this.flow?.settings?.cta_url || '/kontakt', payload, responsePayload);
            const ctaLabel = this.normalizeCtaLabel(this.flow?.settings?.cta_label, 'Erstgespräch anfragen');
            this.controlsEl.innerHTML = '';
            const ctaButton = this.createButton(ctaLabel, () => {
                window.location.href = ctaUrl;
            });
            this.controlsEl.appendChild(this.wrapControls(ctaButton));
            return true;
        } catch (error) {
            this.addBotBubble(error.message || 'Es gab ein Problem beim Speichern. Bitte versuchen Sie es erneut.');
            return false;
        }
    }

    async extractSuccessPayload(response) {
        try {
            const payload = await response.json();
            return payload && typeof payload === 'object' ? payload : {};
        } catch (e) {
            return {};
        }
    }

    buildLeadCtaUrl(rawUrl, leadPayload = {}, responsePayload = {}) {
        const targetUrl = this.normalizeString(rawUrl, '/kontakt');
        const baseOrigin = window.location.origin;

        let parsedUrl;
        try {
            parsedUrl = new URL(targetUrl, baseOrigin);
        } catch (e) {
            return targetUrl;
        }

        const isInternalUrl = parsedUrl.origin === baseOrigin;
        if (isInternalUrl) {
            const vorgangId = this.normalizeString(responsePayload?.lead_id, '');
            if (vorgangId !== '' && !parsedUrl.searchParams.has('vorgang')) {
                parsedUrl.searchParams.set('vorgang', vorgangId);
            }

            this.appendQueryParamIfMissing(parsedUrl, 'first_name', leadPayload?.first_name);
            this.appendQueryParamIfMissing(parsedUrl, 'last_name', leadPayload?.last_name);
            this.appendQueryParamIfMissing(parsedUrl, 'email', leadPayload?.email);
            this.appendQueryParamIfMissing(parsedUrl, 'phone', leadPayload?.phone);
        }

        if (isInternalUrl) {
            return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        }

        return parsedUrl.toString();
    }

    appendQueryParamIfMissing(parsedUrl, key, value) {
        if (!parsedUrl || !parsedUrl.searchParams || parsedUrl.searchParams.has(key)) {
            return;
        }

        const normalizedValue = this.normalizeString(value, '');
        if (normalizedValue === '') {
            return;
        }

        parsedUrl.searchParams.set(key, normalizedValue);
    }

    buildAnswerPayload() {
        const payload = {};
        Object.keys(this.answers).forEach((blockId) => {
            const numericBlockId = Number.parseInt(blockId, 10);
            if (!Number.isInteger(numericBlockId) || numericBlockId <= 0) {
                return;
            }
            payload[blockId] = this.answers[blockId].value;
        });
        return payload;
    }

    buildSummary() {
        const summary = [];
        const chapterMap = {};

        this.blocks.forEach((block) => {
            if (block.key === 'time_check') {
                return;
            }

            const answer = this.answers[block.id];
            if (!answer) {
                return;
            }

            if (!block.chapter_id) {
                return;
            }

            if (!chapterMap[block.chapter_id]) {
                chapterMap[block.chapter_id] = {
                    chapterTitle: block.chapter_title,
                    items: [],
                };
                summary.push(chapterMap[block.chapter_id]);
            }

            chapterMap[block.chapter_id].items.push({
                question: block.question,
                answer: answer.display,
            });
        });

        const pricingPreview = this.computeLocalPricePreview();
        if (pricingPreview) {
            summary.push({
                chapterTitle: 'Paket & Preisindikation',
                items: [
                    {
                        question: 'Vorläufige Preisindikation',
                        answer: this.formatPricePreview(pricingPreview),
                    },
                ],
            });
        }

        return summary;
    }

    injectEntryTimeCheckIfNeeded(blocks) {
        const settings = this.flow?.settings || {};
        if (!this.normalizeBoolean(settings.abort_on_no, false)) {
            return blocks;
        }

        const hasTimeCheckBlock = blocks.some(
            (block) => block?.key === 'time_check' && block?.type === 'yes_no',
        );
        if (hasTimeCheckBlock) {
            return blocks;
        }

        const syntheticTimeCheckBlock = {
            id: '__time_check__',
            key: 'time_check',
            type: 'yes_no',
            question: this.normalizeString(settings.time_check_question, 'Haben Sie gerade 2 Minuten Zeit?'),
            helper_text: null,
            placeholder: null,
            is_required: true,
            config: {
                yes_label: this.normalizeString(settings.time_check_yes_label, 'Ja, gerne'),
                no_label: this.normalizeString(settings.time_check_no_label, 'Im Moment nicht'),
            },
            options: [],
            chapter_id: null,
            chapter_title: null,
        };

        const nextBlocks = Array.isArray(blocks) ? blocks.slice() : [];
        let insertIndex = 0;
        while (insertIndex < nextBlocks.length && nextBlocks[insertIndex]?.type === 'message') {
            insertIndex += 1;
        }

        nextBlocks.splice(insertIndex, 0, syntheticTimeCheckBlock);

        return nextBlocks;
    }

    handleAnswer(block, value, display) {
        this.answers[block.id] = {
            value,
            display,
        };
        if (this.isAnswerableBlock(block)) {
            this.answeredBlockIds.add(block.id);
        }
        this.addUserBubble(display);
        this.trackChatAnswer(block, value, display);
        this.clearControls();

        if (this.shouldAbortFlow(block, value)) {
            this.renderAbortState();
            return;
        }

        this.next();
    }

    next() {
        if (this.flowAborted) {
            return;
        }

        this.currentIndex += 1;
        if (this.currentIndex < this.blocks.length) {
            this.showBlock(this.blocks[this.currentIndex]);
            return;
        }

        this.updateProgress();
        if (!this.leadFormRendered) {
            this.renderLeadForm();
        }
    }

    async showTyping() {
        const bubble = document.createElement('div');
        bubble.className = 'chat-bubble chat-bubble--left';
        bubble.innerHTML = `
            <div class="chat-typing">
                <span class="chat-typing__dot"></span>
                <span class="chat-typing__dot"></span>
                <span class="chat-typing__dot"></span>
            </div>`;
        this.messagesEl.appendChild(bubble);
        await this.sleep(600);
        bubble.remove();
    }

    addBotBubble(text) {
        if (!text) {
            return;
        }
        const bubble = document.createElement('div');
        bubble.className = 'chat-bubble chat-bubble--left';
        bubble.textContent = text;
        this.messagesEl.appendChild(bubble);
        this.scrollMessagesToBottom();
    }

    addUserBubble(text) {
        const bubble = document.createElement('div');
        bubble.className = 'chat-bubble chat-bubble--right';
        bubble.textContent = text || '—';
        this.messagesEl.appendChild(bubble);
        this.scrollMessagesToBottom();
    }

    addBotHelper(text) {
        const helper = document.createElement('div');
        helper.className = 'chat-helper';
        helper.textContent = text;
        this.messagesEl.appendChild(helper);
        this.scrollMessagesToBottom();
    }

    addChapterDivider(title) {
        if (!title) {
            return;
        }

        const divider = document.createElement('div');
        divider.className = 'chat-chapter-divider';
        divider.innerHTML = `<span>${title}</span>`;
        this.messagesEl.appendChild(divider);
        this.scrollMessagesToBottom();
    }

    shouldAbortFlow(block, value) {
        const settings = this.flow?.settings || {};
        const shouldAbortOnNo = this.normalizeBoolean(settings.abort_on_no, false);
        if (!shouldAbortOnNo) {
            return false;
        }

        if (!block || block.type !== 'yes_no' || block.key !== 'time_check') {
            return false;
        }

        const normalized = String(value || '').trim().toLowerCase();
        return ['no', 'nein', 'false', '0'].includes(normalized);
    }

    renderAbortState() {
        this.flowAborted = true;
        this.trackChatAbort('abort_on_no');
        this.unbindLifecycleTracking();

        const settings = this.flow?.settings || {};
        const message = String(settings.abort_message || '').trim()
            || 'Kein Problem. Wenn Sie später starten möchten, sind wir mit einem Klick erreichbar.';
        const ctaLabel = String(settings.abort_cta_label || settings.cta_label || 'Zum Kontakt').trim();
        const ctaUrl = String(settings.abort_cta_url || settings.cta_url || '/kontakt').trim();

        this.addBotBubble(message);
        this.controlsEl.innerHTML = '';

        if (ctaLabel && ctaUrl) {
            const ctaButton = this.createButton(ctaLabel, () => {
                window.location.href = ctaUrl;
            });
            this.controlsEl.appendChild(this.wrapControls(ctaButton));
        }
    }

    clearControls() {
        this.controlsEl.innerHTML = '';
    }

    isAnswerableBlock(block) {
        if (!block) {
            return false;
        }

        return !['message', 'summary'].includes(block.type);
    }

    updateProgress(currentBlock = null) {
        if (!this.progressLabelEl || !this.progressFillEl || !this.progressTrackEl) {
            return;
        }

        const total = this.progressBlocks.length;
        if (!total) {
            this.progressLabelEl.textContent = 'Schritt 0 / 0';
            this.progressFillEl.style.width = '0%';
            this.progressTrackEl.setAttribute('aria-valuenow', '0');
            return;
        }

        const completed = this.answeredBlockIds.size;
        const currentStep = Math.min(total, completed + (this.isAnswerableBlock(currentBlock) ? 1 : 0));
        const progress = Math.round((completed / total) * 100);

        this.progressLabelEl.textContent = `Schritt ${currentStep} / ${total}`;
        this.progressFillEl.style.width = `${progress}%`;
        this.progressTrackEl.setAttribute('aria-valuenow', String(progress));
    }

    setProgressComplete() {
        if (!this.progressLabelEl || !this.progressFillEl || !this.progressTrackEl) {
            return;
        }

        const total = this.progressBlocks.length;
        this.progressLabelEl.textContent = `Schritt ${total} / ${total}`;
        this.progressFillEl.style.width = '100%';
        this.progressTrackEl.setAttribute('aria-valuenow', '100');
    }

    async extractErrorMessage(response) {
        try {
            const payload = await response.json();
            if (payload?.message) {
                return payload.message;
            }
            if (payload?.errors) {
                const firstField = Object.keys(payload.errors)[0];
                const firstError = payload.errors[firstField]?.[0];
                if (firstError) {
                    return firstError;
                }
            }
        } catch (e) {
            // Non-JSON response fallback.
        }

        return 'Lead konnte nicht gespeichert werden.';
    }

    hasUploadFiles() {
        return Object.values(this.uploadFilesByBlock).some(
            (files) => Array.isArray(files) && files.length > 0,
        );
    }

    buildLeadFormData(payload) {
        const formData = new FormData();

        Object.entries(payload || {}).forEach(([key, value]) => {
            if (key === 'answers') {
                return;
            }

            if (value === null || value === undefined || value === '') {
                return;
            }

            if (typeof value === 'boolean') {
                formData.append(key, value ? '1' : '0');
                return;
            }

            formData.append(key, String(value));
        });

        formData.append('answers', JSON.stringify(payload?.answers || {}));

        Object.entries(this.uploadFilesByBlock).forEach(([blockId, files]) => {
            if (!Array.isArray(files)) {
                return;
            }

            files.forEach((file) => {
                formData.append(`upload_files[${blockId}][]`, file, file.name);
            });
        });

        return formData;
    }

    normalizeUploadMimeTypes(raw) {
        const fallback = ['application/pdf', 'image/jpeg', 'image/png'];
        let values = raw;
        if (typeof values === 'string') {
            values = values.split(',');
        }

        if (!Array.isArray(values)) {
            return fallback;
        }

        const normalized = values
            .map((value) => String(value || '').trim().toLowerCase())
            .filter((value) => value !== '');

        return normalized.length > 0 ? normalized : fallback;
    }

    toFileInputAccept(mimeTypes) {
        const extensions = [];
        mimeTypes.forEach((mimeType) => {
            if (mimeType === 'application/pdf') {
                extensions.push('.pdf');
            }

            if (mimeType === 'image/jpeg') {
                extensions.push('.jpg', '.jpeg');
            }

            if (mimeType === 'image/png') {
                extensions.push('.png');
            }
        });

        return Array.from(new Set([...mimeTypes, ...extensions])).join(',');
    }

    isAllowedUploadMimeType(file, allowedMimeTypes) {
        const mimeType = String(file?.type || '').toLowerCase().trim();
        if (mimeType !== '') {
            for (const allowed of allowedMimeTypes) {
                if (allowed === '*/*' || allowed === mimeType) {
                    return true;
                }

                if (allowed.endsWith('/*') && mimeType.startsWith(allowed.slice(0, -1))) {
                    return true;
                }
            }
        }

        const name = String(file?.name || '').toLowerCase();
        if (name.endsWith('.pdf') && allowedMimeTypes.includes('application/pdf')) {
            return true;
        }

        if ((name.endsWith('.jpg') || name.endsWith('.jpeg')) && allowedMimeTypes.includes('image/jpeg')) {
            return true;
        }

        if (name.endsWith('.png') && allowedMimeTypes.includes('image/png')) {
            return true;
        }

        return false;
    }

    formatFileSize(sizeInBytes) {
        const bytes = Math.max(0, Number(sizeInBytes || 0));
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        if (bytes < 1024 * 1024) {
            return `${Math.round(bytes / 1024)} KB`;
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    wrapControls(element) {
        const wrapper = document.createElement('div');
        wrapper.className = 'chat-controls';
        wrapper.appendChild(element);
        return wrapper;
    }

    createControlsContainer() {
        const wrapper = document.createElement('div');
        wrapper.className = 'chat-controls';
        return wrapper;
    }

    createButton(label, onClick, extraClass = '') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `chat-button ${extraClass}`.trim();
        button.textContent = label;
        button.addEventListener('click', onClick);
        return button;
    }

    setButtonLoading(button, isLoading, loadingLabel = 'Wird gesendet ...') {
        if (!button) {
            return;
        }

        const originalLabel = button.dataset.originalLabel || button.textContent || '';
        button.dataset.originalLabel = originalLabel;

        if (!isLoading) {
            button.classList.remove('is-loading');
            button.disabled = false;
            button.textContent = originalLabel;
            return;
        }

        button.classList.add('is-loading');
        button.disabled = true;
        button.textContent = '';

        const spinner = document.createElement('span');
        spinner.className = 'chat-button__spinner';
        spinner.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.className = 'chat-button__label';
        label.textContent = loadingLabel;

        button.appendChild(spinner);
        button.appendChild(label);
    }

    createChoiceButton(option, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'chat-choice__option';
        if (option.media_url) {
            button.classList.add('chat-choice__option--with-media');
            const img = document.createElement('img');
            img.src = option.media_url;
            img.alt = option.label || '';
            button.appendChild(img);
        }
        const label = document.createElement('span');
        label.textContent = option.label;
        button.appendChild(label);
        button.addEventListener('click', onClick);
        return button;
    }

    createPackageCard(pkg, allPackages, onClick) {
        const card = document.createElement('article');
        card.className = 'chat-package-card';
        card.setAttribute('role', 'button');
        card.tabIndex = 0;
        card.appendChild(this.createCardCheck());
        this.appendCardMedia(card, this.resolvePackageMediaUrl(pkg), pkg.name || 'Paket');

        const heading = document.createElement('div');
        heading.className = 'chat-package-card__heading';

        const title = document.createElement('strong');
        title.textContent = pkg.name || 'Paket';
        heading.appendChild(title);

        if (pkg.short_label) {
            const badge = document.createElement('span');
            badge.className = 'chat-package-card__badge';
            badge.textContent = pkg.short_label;
            heading.appendChild(badge);
        }

        card.appendChild(heading);

        if (pkg.description) {
            const description = document.createElement('p');
            description.className = 'chat-package-card__description';
            description.textContent = pkg.description;
            card.appendChild(description);
        }

        if (Array.isArray(pkg.usp_points) && pkg.usp_points.length > 0) {
            const ul = document.createElement('ul');
            ul.className = 'chat-package-card__usp';
            pkg.usp_points.forEach((point) => {
                const li = document.createElement('li');
                li.textContent = point;
                ul.appendChild(li);
            });
            card.appendChild(ul);
        }

        const pricing = this.formatPricingRange(pkg.pricing);
        if (pricing) {
            const price = document.createElement('div');
            price.className = 'chat-package-card__price';
            price.textContent = pricing;
            card.appendChild(price);
        }

        const detailsButton = this.createPackageDetailsButton(pkg, allPackages);
        if (detailsButton) {
            card.appendChild(detailsButton);
        }

        card.addEventListener('click', onClick);
        card.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }

            event.preventDefault();
            onClick();
        });

        return card;
    }

    createAddonCard(addon, isAllowed, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'chat-package-card chat-package-card--addon';
        button.disabled = !isAllowed;
        button.appendChild(this.createCardCheck());
        this.appendCardMedia(button, this.resolveMediaUrl(addon), addon.name || 'Add-on');

        const heading = document.createElement('div');
        heading.className = 'chat-package-card__heading';

        const title = document.createElement('strong');
        title.textContent = addon.name || 'Add-on';
        heading.appendChild(title);

        if (addon.category) {
            const chip = document.createElement('span');
            chip.className = 'chat-package-card__chip';
            chip.textContent = this.formatCategoryLabel(addon.category);
            heading.appendChild(chip);
        }

        button.appendChild(heading);

        if (addon.description) {
            const description = document.createElement('p');
            description.className = 'chat-package-card__description';
            description.textContent = addon.description;
            button.appendChild(description);
        }

        const pricing = this.formatPricingRange(addon.pricing);
        if (pricing) {
            const price = document.createElement('div');
            price.className = 'chat-package-card__price';
            price.textContent = pricing;
            button.appendChild(price);
        }

        if (!isAllowed) {
            const note = document.createElement('div');
            note.className = 'chat-package-card__note';
            note.textContent = 'Nicht verfügbar für die aktuelle Auswahl';
            button.appendChild(note);
        }

        button.addEventListener('click', onClick);

        return button;
    }

    createPackageDetailsButton(pkg, allPackages = []) {
        const packageItems = Array.isArray(pkg?.package_items) ? pkg.package_items : [];
        if (packageItems.length === 0) {
            return null;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'chat-package-card__details';
        const icon = document.createElement('span');
        icon.className = 'chat-package-card__details-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = 'i';
        button.appendChild(icon);

        const label = document.createElement('span');
        label.textContent = 'Alle Leistungen';
        button.appendChild(label);

        button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openPackageDetailsModal(pkg, allPackages);
        });

        return button;
    }

    ensurePackageDetailsModal() {
        if (this.packageModalEl) {
            return;
        }

        const modal = document.createElement('div');
        modal.className = 'chat-package-modal';
        modal.hidden = true;
        modal.innerHTML = `
            <div class="chat-package-modal__backdrop" data-chat-package-modal-close></div>
            <div class="chat-package-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="${this.packageModalTitleId}">
                <button type="button" class="chat-package-modal__close" data-chat-package-modal-close aria-label="Schließen">×</button>
                <h4 class="chat-package-modal__title" id="${this.packageModalTitleId}">Alle Leistungen und Details</h4>
                <div class="chat-package-modal__package-tabs"></div>
                <div class="chat-package-modal__component-tabs"></div>
                <div class="chat-package-modal__content"></div>
            </div>
        `;

        this.packageModalEl = modal;
        this.packageModalCloseEl = modal.querySelector('.chat-package-modal__close');
        this.packageModalPackageTabsEl = modal.querySelector('.chat-package-modal__package-tabs');
        this.packageModalComponentTabsEl = modal.querySelector('.chat-package-modal__component-tabs');
        this.packageModalContentEl = modal.querySelector('.chat-package-modal__content');

        modal.querySelectorAll('[data-chat-package-modal-close]').forEach((node) => {
            node.addEventListener('click', () => this.closePackageDetailsModal());
        });

        this.packageModalKeyHandler = (event) => {
            if (event.key === 'Escape') {
                this.closePackageDetailsModal();
            }
        };

        document.body.appendChild(modal);
    }

    openPackageDetailsModal(pkg, allPackages = []) {
        this.ensurePackageDetailsModal();
        if (!this.packageModalEl || !this.packageModalPackageTabsEl || !this.packageModalComponentTabsEl || !this.packageModalContentEl) {
            return;
        }

        const packages = (Array.isArray(allPackages) ? allPackages : [])
            .filter((entry) => Array.isArray(entry?.package_items) && entry.package_items.length > 0);
        const normalizedPackages = packages.length > 0 ? packages : [pkg].filter(Boolean);
        if (normalizedPackages.length === 0) {
            return;
        }

        const activePackageId = Number(pkg?.id || normalizedPackages[0].id);
        this.packageModalState = {
            packages: normalizedPackages,
            activePackageId,
            groups: [],
            activeGroupKey: null,
        };

        this.renderPackageModalPackageTabs();
        this.activatePackageTab(activePackageId);

        this.packageModalEl.hidden = false;
        this.packageModalEl.classList.add('is-open');
        document.body.classList.add('chat-package-modal-open');
        document.addEventListener('keydown', this.packageModalKeyHandler);
        this.packageModalCloseEl?.focus();
    }

    closePackageDetailsModal() {
        if (!this.packageModalEl) {
            return;
        }

        this.packageModalEl.classList.remove('is-open');
        this.packageModalEl.hidden = true;
        document.body.classList.remove('chat-package-modal-open');
        if (this.packageModalKeyHandler) {
            document.removeEventListener('keydown', this.packageModalKeyHandler);
        }
    }

    renderPackageModalPackageTabs() {
        if (!this.packageModalPackageTabsEl) {
            return;
        }

        this.packageModalPackageTabsEl.innerHTML = '';
        this.packageModalState.packages.forEach((pkg) => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'chat-package-modal__package-tab';
            tab.textContent = pkg.name || 'Paket';
            tab.addEventListener('click', () => this.activatePackageTab(Number(pkg.id)));
            this.packageModalPackageTabsEl.appendChild(tab);
        });
    }

    activatePackageTab(packageId) {
        const selectedPackage = this.packageModalState.packages.find((pkg) => Number(pkg.id) === Number(packageId))
            || this.packageModalState.packages[0];

        if (!selectedPackage) {
            return;
        }

        this.packageModalState.activePackageId = Number(selectedPackage.id);

        if (this.packageModalPackageTabsEl) {
            Array.from(this.packageModalPackageTabsEl.children).forEach((tabNode, index) => {
                const pkg = this.packageModalState.packages[index];
                const isActive = Number(pkg?.id) === this.packageModalState.activePackageId;
                tabNode.classList.toggle('is-active', isActive);
            });
        }

        const groups = this.groupPackageItemsForModal(selectedPackage.package_items || []);
        this.packageModalState.groups = groups;

        if (groups.length === 0) {
            this.packageModalState.activeGroupKey = null;
            this.renderPackageModalComponentTabs();
            this.renderPackageModalContent();
            return;
        }

        const currentKey = this.packageModalState.activeGroupKey;
        const hasCurrent = groups.some((group) => group.key === currentKey);
        this.packageModalState.activeGroupKey = hasCurrent ? currentKey : groups[0].key;
        this.renderPackageModalComponentTabs();
        this.renderPackageModalContent();
    }

    activateComponentTab(groupKey) {
        this.packageModalState.activeGroupKey = groupKey;
        this.renderPackageModalComponentTabs();
        this.renderPackageModalContent();
    }

    renderPackageModalComponentTabs() {
        if (!this.packageModalComponentTabsEl) {
            return;
        }

        this.packageModalComponentTabsEl.innerHTML = '';
        const groups = this.packageModalState.groups || [];

        if (groups.length === 0) {
            this.packageModalComponentTabsEl.classList.add('is-empty');
            return;
        }

        this.packageModalComponentTabsEl.classList.remove('is-empty');

        groups.forEach((group) => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'chat-package-modal__component-tab';
            tab.classList.toggle('is-active', group.key === this.packageModalState.activeGroupKey);
            tab.innerHTML = `
                <span class="chat-package-modal__component-icon"><i class="${group.icon}" aria-hidden="true"></i></span>
                <span class="chat-package-modal__component-label">${group.label}</span>
            `;
            tab.addEventListener('click', () => this.activateComponentTab(group.key));
            this.packageModalComponentTabsEl.appendChild(tab);
        });
    }

    renderPackageModalContent() {
        if (!this.packageModalContentEl) {
            return;
        }

        this.packageModalContentEl.innerHTML = '';

        const groups = this.packageModalState.groups || [];
        if (groups.length === 0) {
            const empty = document.createElement('p');
            empty.className = 'chat-package-modal__empty';
            empty.textContent = 'Für dieses Paket sind aktuell keine Details hinterlegt.';
            this.packageModalContentEl.appendChild(empty);
            return;
        }

        const activeGroup = groups.find((group) => group.key === this.packageModalState.activeGroupKey) || groups[0];
        if (!activeGroup) {
            return;
        }

        const groupItems = Array.isArray(activeGroup.items) ? activeGroup.items : [];
        const primaryItem = this.resolvePrimaryItemForGroup(groupItems);
        if (!primaryItem) {
            const empty = document.createElement('p');
            empty.className = 'chat-package-modal__empty';
            empty.textContent = 'Für diese Komponente sind aktuell keine Details hinterlegt.';
            this.packageModalContentEl.appendChild(empty);
            return;
        }

        const detail = document.createElement('article');
        detail.className = 'chat-package-modal__detail';

        const mediaUrl = this.resolveMediaUrl(primaryItem);
        if (mediaUrl) {
            const media = document.createElement('div');
            media.className = 'chat-package-modal__detail-media';
            const image = document.createElement('img');
            image.src = mediaUrl;
            image.alt = primaryItem.name || '';
            image.loading = 'lazy';
            image.decoding = 'async';
            media.appendChild(image);
            detail.appendChild(media);
        }

        const content = document.createElement('div');
        content.className = 'chat-package-modal__detail-content';

        const title = document.createElement('h5');
        title.className = 'chat-package-modal__detail-title';
        title.textContent = primaryItem.name || 'Komponente';
        content.appendChild(title);

        const points = this.buildItemDetailRows(primaryItem);
        if (points.length > 0) {
            const list = document.createElement('ul');
            list.className = 'chat-package-modal__detail-points';
            points.forEach((point) => {
                const li = document.createElement('li');
                li.textContent = point;
                list.appendChild(li);
            });
            content.appendChild(list);
        } else {
            const fallback = document.createElement('p');
            fallback.className = 'chat-package-modal__detail-fallback';
            fallback.textContent = 'Für diese Komponente sind aktuell keine weiteren Details hinterlegt.';
            content.appendChild(fallback);
        }

        if (groupItems.length > 0) {
            const included = document.createElement('div');
            included.className = 'chat-package-modal__included';

            const includedTitle = document.createElement('h6');
            includedTitle.className = 'chat-package-modal__included-title';
            includedTitle.textContent = 'Enthaltene Komponenten';
            included.appendChild(includedTitle);

            const includedList = document.createElement('ul');
            includedList.className = 'chat-package-modal__included-list';

            groupItems.forEach((item) => {
                const li = document.createElement('li');

                const parts = [];
                const name = String(item?.name || '').trim();
                if (name) {
                    parts.push(name);
                }

                const quantityValue = Number(item?.quantity);
                if (!Number.isNaN(quantityValue) && quantityValue > 0) {
                    parts.push(`x ${quantityValue.toLocaleString('de-DE')}`);
                }

                const pricingLabel = String(item?.pricing_label || '').trim();
                if (pricingLabel) {
                    parts.push(pricingLabel);
                }

                li.textContent = parts.join(' • ') || 'Komponente';
                if (Number(item?.id) === Number(primaryItem.id)) {
                    li.classList.add('is-primary');
                }
                includedList.appendChild(li);
            });

            included.appendChild(includedList);
            content.appendChild(included);
        }

        detail.appendChild(content);
        this.packageModalContentEl.appendChild(detail);
    }

    groupPackageItemsForModal(items) {
        const groups = [];
        const map = new Map();

        (Array.isArray(items) ? items : []).forEach((item) => {
            const meta = this.resolveComponentGroupMeta(item);
            if (!map.has(meta.key)) {
                const entry = {
                    key: meta.key,
                    label: meta.label,
                    icon: meta.icon,
                    items: [],
                };
                map.set(meta.key, entry);
                groups.push(entry);
            }

            map.get(meta.key).items.push(item);
        });

        return groups;
    }

    resolveComponentGroupMeta(item) {
        const categoryRaw = String(item?.category || '').toLowerCase().trim();
        const normalized = categoryRaw
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[_/|]+/g, '-')
            .replace(/\s+/g, '-');
        const itemType = String(item?.item_type || '').toLowerCase();

        const containsAny = (needles) => needles.some((needle) => normalized.includes(needle));

        if (containsAny(['inverter', 'wechselrichter', 'wr'])) {
            return { key: 'inverter', label: 'Wechselrichter', icon: 'bi bi-pc-display' };
        }

        if (containsAny(['module', 'modul', 'pv-anlage', 'pv-module', 'solarmodul', 'solarpanel', 'panel'])) {
            return { key: 'module', label: 'PV-Anlage', icon: 'bi bi-grid-3x3-gap' };
        }

        if (containsAny(['storage', 'speicher', 'batterie', 'battery', 'akku'])) {
            return { key: 'storage', label: 'Batteriespeicher', icon: 'bi bi-battery-half' };
        }

        if (containsAny(['monitoring', 'service', 'energiemanagement', 'energy', 'wallbox', 'backup', 'ersatzstrom', 'notstrom', 'safety', 'smartmeter'])) {
            return { key: 'energy', label: 'Energiemanagement', icon: 'bi bi-clipboard-data' };
        }

        if (itemType === 'addon') {
            return { key: 'options', label: 'Preise & Optionen', icon: 'bi bi-plus-circle' };
        }

        return { key: 'options', label: 'Preise & Optionen', icon: 'bi bi-plus-circle' };
    }

    buildItemDetailRows(item) {
        const rows = [];
        const description = String(item?.description || '').trim();
        if (description) {
            const chunks = description
                .split(/\r?\n|[;•]+/g)
                .map((entry) => entry.trim())
                .filter(Boolean);
            rows.push(...(chunks.length > 0 ? chunks : [description]));
        }

        if (item?.pricing_label) {
            rows.push(`Preis: ${item.pricing_label}`);
        }

        if (item?.quantity !== null && item?.quantity !== undefined && Number(item.quantity) > 0) {
            rows.push(`Menge: ${Number(item.quantity).toLocaleString('de-DE')}`);
        }

        return rows;
    }

    resolvePrimaryItemForGroup(items) {
        const candidates = Array.isArray(items) ? items : [];
        if (candidates.length === 0) {
            return null;
        }

        const scoreFor = (item) => {
            let score = 0;
            if (this.resolveMediaUrl(item)) {
                score += 20;
            }
            if (String(item?.description || '').trim() !== '') {
                score += 10;
            }
            if (String(item?.pricing_label || '').trim() !== '') {
                score += 4;
            }
            if (String(item?.name || '').trim() !== '') {
                score += 2;
            }

            return score;
        };

        return candidates
            .slice()
            .sort((a, b) => scoreFor(b) - scoreFor(a))[0];
    }

    updateStars(wrapper, value) {
        const stars = wrapper.querySelectorAll('.chat-rating__star');
        stars.forEach((star, index) => {
            star.classList.toggle('is-active', index < value);
        });
    }

    starIcon() {
        return `
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2l2.9 6.1 6.7.9-4.8 4.7 1.2 6.7L12 17.8 6 20.4l1.2-6.7L2.4 9l6.7-.9L12 2z"></path>
            </svg>
        `;
    }

    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    scrollMessagesToBottom() {
        const scrollContainer = this.element || this.bodyEl || this.messagesScrollEl || this.messagesEl;
        if (!scrollContainer) {
            return;
        }

        window.requestAnimationFrame(() => {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
            this.updateTopFadeState(scrollContainer);
        });
    }

    bindScrollFade(container) {
        if (this.scrollContainerEl === container && this.scrollListener) {
            return;
        }

        this.unbindScrollFade();

        this.scrollContainerEl = container;
        this.scrollListener = () => this.updateTopFadeState(container);
        container.addEventListener('scroll', this.scrollListener, { passive: true });
    }

    unbindScrollFade() {
        if (this.scrollContainerEl && this.scrollListener) {
            this.scrollContainerEl.removeEventListener('scroll', this.scrollListener);
        }

        this.scrollContainerEl = null;
        this.scrollListener = null;
    }

    updateTopFadeState(container) {
        container.classList.toggle('chat-widget__is-scrolled', container.scrollTop > 8);
    }

    bindLifecycleTracking() {
        if (this.lifecycleTrackingBound) {
            return;
        }

        this.boundPageHideHandler = () => {
            this.trackChatDropoff('pagehide');
        };
        this.boundBeforeUnloadHandler = () => {
            this.trackChatDropoff('beforeunload');
        };
        this.boundVisibilityChangeHandler = () => {
            if (document.visibilityState === 'hidden') {
                this.trackChatDropoff('visibility_hidden');
            }
        };

        window.addEventListener('pagehide', this.boundPageHideHandler, { passive: true });
        window.addEventListener('beforeunload', this.boundBeforeUnloadHandler, { passive: true });
        document.addEventListener('visibilitychange', this.boundVisibilityChangeHandler, { passive: true });
        this.lifecycleTrackingBound = true;
    }

    unbindLifecycleTracking() {
        if (!this.lifecycleTrackingBound) {
            return;
        }

        if (this.boundPageHideHandler) {
            window.removeEventListener('pagehide', this.boundPageHideHandler);
        }
        if (this.boundBeforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
        }
        if (this.boundVisibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.boundVisibilityChangeHandler);
        }

        this.boundPageHideHandler = null;
        this.boundBeforeUnloadHandler = null;
        this.boundVisibilityChangeHandler = null;
        this.lifecycleTrackingBound = false;
    }

    updateTrackingContext(block) {
        const stepIndex = this.progressBlocks.findIndex(
            (progressBlock) => String(progressBlock.id) === String(block?.id),
        );
        const stepNumber = stepIndex >= 0 ? stepIndex + 1 : null;
        const stepTotal = this.progressBlocks.length;

        this.activeTrackingContext = {
            blockId: block?.id ?? null,
            blockKey: this.cleanTrackingValue(block?.key || ''),
            blockType: this.cleanTrackingValue(block?.type || ''),
            chapterTitle: this.cleanTrackingValue(block?.chapter_title || ''),
            question: this.truncateTrackingValue(this.cleanTrackingValue(block?.question || ''), 110),
            stepNumber,
            stepTotal,
            answeredCount: this.answeredBlockIds.size,
        };
    }

    trackChatStart() {
        if (this.chatStartedTracked) {
            return;
        }

        this.chatStartedTracked = true;
        this.trackMatomoEvent('started');
    }

    trackChatCompletion() {
        if (this.chatCompletedTracked) {
            return;
        }

        this.chatCompletedTracked = true;
        this.trackMatomoEvent('completed');
    }

    trackChatAbort(reason = 'abort') {
        if (this.chatAbortedTracked) {
            return;
        }

        this.chatAbortedTracked = true;
        this.trackMatomoEvent('aborted', reason);
    }

    trackChatAnswer(block, value, display) {
        if (!this.isAnswerableBlock(block)) {
            return;
        }

        this.updateTrackingContext(block);
        this.trackMatomoEvent('answered', this.describeAnswerTrackingValue(block, value, display));
    }

    trackChatDropoff(reason = 'unknown') {
        if (!this.chatStartedTracked || this.chatCompletedTracked || this.chatAbortedTracked || this.chatDropoffTracked) {
            return;
        }

        this.chatDropoffTracked = true;
        this.trackMatomoEvent('dropoff', reason);
    }

    trackMatomoEvent(action, reason = '') {
        const queue = this.getMatomoQueue();
        if (!queue) {
            return;
        }

        const eventPayload = this.buildMatomoEventPayload(action, reason);
        queue.push(['trackEvent', eventPayload.category, eventPayload.action, eventPayload.name, eventPayload.value]);
    }

    getMatomoQueue() {
        if (typeof window === 'undefined') {
            return null;
        }

        if (!window._paq) {
            window._paq = [];
        }

        return typeof window._paq.push === 'function' ? window._paq : null;
    }

    buildMatomoEventPayload(action, reason = '') {
        const context = this.activeTrackingContext || {};
        const stepLabel = this.buildMatomoStepLabel(context);
        const question = context.question || 'Unbekannte Frage';
        const chapterTitle = context.chapterTitle || '';
        const elapsedSeconds = this.getChatElapsedSeconds();
        const flowLabel = this.getFlowLabel();
        const reasonLabel = this.mapDropoffReasonLabel(reason);
        const questionLine = chapterTitle !== ''
            ? `${question} (${chapterTitle})`
            : question;
        const safeQuestionLine = this.truncateTrackingValue(questionLine, 170);

        if (action === 'dropoff') {
            return {
                category: 'Chat Abbruch',
                action: stepLabel,
                name: this.truncateTrackingValue(
                    `${safeQuestionLine} · ${reasonLabel}`,
                    170,
                ),
                value: elapsedSeconds,
            };
        }

        if (action === 'aborted') {
            return {
                category: 'Chat Abbruch',
                action: stepLabel,
                name: this.truncateTrackingValue(
                    `${safeQuestionLine} · Chat wurde bewusst beendet`,
                    170,
                ),
                value: elapsedSeconds,
            };
        }

        if (action === 'answered') {
            return {
                category: 'Chat Antworten',
                action: `${stepLabel} beantwortet`,
                name: this.truncateTrackingValue(
                    `${safeQuestionLine} · Antwort: ${reason || 'keine Angabe'}`,
                    170,
                ),
                value: elapsedSeconds,
            };
        }

        if (action === 'completed') {
            return {
                category: 'Chat Verlauf',
                action: 'Abgeschlossen',
                name: this.truncateTrackingValue(
                    `${this.answeredBlockIds.size} Fragen beantwortet · ${flowLabel}`,
                    170,
                ),
                value: elapsedSeconds,
            };
        }

        return {
            category: 'Chat Verlauf',
            action: 'Gestartet',
            name: this.truncateTrackingValue(flowLabel, 140),
            value: 0,
        };
    }

    buildMatomoStepLabel(context) {
        const stepNumber = Number.isInteger(context?.stepNumber) ? context.stepNumber : null;
        const stepTotal = Number.isInteger(context?.stepTotal) ? context.stepTotal : null;

        if (stepNumber && stepTotal && stepTotal > 0) {
            return `Schritt ${stepNumber} von ${stepTotal}`;
        }

        // Nicht beantwortbare Bloecke (Einleitung, Zusammenfassung) stehen nicht in
        // progressBlocks und haben daher keine Schrittnummer. Frueher lieferten sie
        // "Schritt unbekannt" -- das hat den teuersten Absprung des Funnels verdeckt
        // (Abbruch auf der Zusammenfassung nach 13 beantworteten Fragen).
        //
        // WICHTIG: Diese Labels duerfen das Wort "beantwortet" NICHT enthalten.
        // Matomo-Ziel 2 matcht auf event_action contains "beantwortet" -- ein
        // Abbruch-Event darf niemals die Engagement-Conversion ausloesen.
        const total = Number.isInteger(stepTotal) && stepTotal > 0
            ? stepTotal
            : this.progressBlocks.length;

        const answered = Number.isInteger(context?.answeredCount)
            ? context.answeredCount
            : this.answeredBlockIds.size;

        if (total > 0 && answered >= total) {
            return `Zusammenfassung (nach Frage ${total})`;
        }

        if (answered <= 0) {
            return 'Einstieg (vor Frage 1)';
        }

        return `Zwischenschritt (nach Frage ${answered})`;
    }

    getFlowLabel() {
        const flowName = this.cleanTrackingValue(this.flow?.name || '');
        const flowSlug = this.cleanTrackingValue(this.flowSlug || '');

        if (flowName !== '') {
            return `Flow: ${flowName}`;
        }

        if (flowSlug !== '') {
            return `Flow: ${flowSlug}`;
        }

        return 'Flow: Unbekannt';
    }

    mapDropoffReasonLabel(reason) {
        const normalized = String(reason || '').trim().toLowerCase();
        if (['pagehide', 'beforeunload', 'visibility_hidden'].includes(normalized)) {
            return 'Seite wurde verlassen';
        }
        if (normalized === 'abort_on_no') {
            return 'Abbruch nach Nein-Antwort';
        }
        if (normalized === 'timeout') {
            return 'Chat ohne Abschluss beendet';
        }

        return 'Chat ohne Abschluss beendet';
    }

    getChatElapsedSeconds() {
        const elapsedMs = Date.now() - this.chatSessionStartedAt;
        if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
            return 0;
        }

        return Math.round(elapsedMs / 1000);
    }

    describeAnswerTrackingValue(block, value, display) {
        const type = String(block?.type || '').toLowerCase();
        const safeDisplay = this.cleanTrackingValue(display || '');
        if (safeDisplay !== '' && ['yes_no', 'single_choice', 'multi_choice', 'package_select', 'addon_select', 'rating'].includes(type)) {
            return this.truncateTrackingValue(safeDisplay, 90);
        }

        if (type === 'yes_no') {
            const normalized = String(value || '').trim().toLowerCase();
            if (['yes', 'ja', 'true', '1'].includes(normalized)) {
                return 'Ja';
            }
            if (['no', 'nein', 'false', '0'].includes(normalized)) {
                return 'Nein';
            }

            return 'Ja/Nein';
        }

        if (type === 'rating') {
            const numericValue = Number.parseInt(String(value ?? ''), 10);
            if (Number.isInteger(numericValue) && numericValue > 0) {
                return `${numericValue} Sterne`;
            }

            return 'Bewertung';
        }

        if (type === 'multi_choice') {
            const selected = Array.isArray(value) ? value.filter((entry) => String(entry || '').trim() !== '') : [];
            if (selected.length === 0) {
                return 'Keine Auswahl';
            }

            return `${selected.length} Optionen ausgewählt`;
        }

        if (type === 'single_choice') {
            return 'Eine Option ausgewählt';
        }

        if (type === 'package_select') {
            return 'Paket gewählt';
        }

        if (type === 'addon_select') {
            const selected = Array.isArray(value) ? value.filter((entry) => String(entry || '').trim() !== '') : [];
            if (selected.length === 0) {
                return 'Keine Add-ons gewählt';
            }

            return `${selected.length} Add-ons gewählt`;
        }

        if (type === 'input') {
            return 'Freitext ausgefüllt';
        }

        if (type === 'file_upload') {
            return 'Datei hochgeladen';
        }

        return 'Antwort gegeben';
    }

    cleanTrackingValue(value) {
        return String(value || '')
            .replace(/\s+/g, ' ')
            .replace(/[|]/g, '/')
            .trim();
    }

    truncateTrackingValue(value, maxLength = 200) {
        const normalized = String(value || '');
        if (normalized.length <= maxLength) {
            return normalized;
        }

        return `${normalized.slice(0, Math.max(0, maxLength - 1))}…`;
    }

    normalizeBoolean(value, fallback = false) {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'number') {
            return value !== 0;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (['1', 'true', 'yes', 'on'].includes(normalized)) {
                return true;
            }
            if (['0', 'false', 'no', 'off'].includes(normalized)) {
                return false;
            }
        }

        return fallback;
    }

    normalizeString(value, fallback = '') {
        const fallbackValue = typeof fallback === 'string' ? fallback : '';
        if (typeof value !== 'string') {
            return this.fixCommonMojibake(fallbackValue);
        }

        const normalized = value.trim();
        return this.fixCommonMojibake(normalized !== '' ? normalized : fallbackValue);
    }

    normalizeConsentText(value, fallback = '') {
        const normalized = this.normalizeString(value, fallback);
        return normalized.replace(/Datenschutzerkl[a-zA-ZÄÖÜäöüß]*/gi, 'Datenschutzerklärung');
    }

    normalizePrivacyPolicyLabel(value, fallback = 'Datenschutzerklärung') {
        const normalized = this.normalizeString(value, fallback);
        return normalized.replace(/Datenschutzerkl[a-zA-ZÄÖÜäöüß]*/gi, 'Datenschutzerklärung');
    }

    normalizeCtaLabel(value, fallback = 'Erstgespräch anfragen') {
        const normalized = this.normalizeString(value, fallback);
        return normalized.replace(/Erstgesrpäch/gi, 'Erstgespräch');
    }

    fixCommonMojibake(value) {
        if (typeof value !== 'string' || value === '') {
            return '';
        }

        const replacements = [
            ['Ã¤', 'ä'],
            ['Ã¶', 'ö'],
            ['Ã¼', 'ü'],
            ['Ã„', 'Ä'],
            ['Ã–', 'Ö'],
            ['Ãœ', 'Ü'],
            ['ÃŸ', 'ß'],
            ['√§', 'ä'],
            ['√∂', 'ö'],
            ['√º', 'ü'],
            ['√Ñ', 'Ä'],
            ['√ñ', 'Ö'],
            ['√ú', 'Ü'],
            ['√ü', 'ß'],
        ];

        let normalized = value;
        replacements.forEach(([from, to]) => {
            normalized = normalized.split(from).join(to);
        });

        return normalized;
    }

    normalizeHexColor(value) {
        if (typeof value !== 'string') {
            return null;
        }

        const color = value.trim();
        if (!/^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/.test(color)) {
            return null;
        }

        if (color.length === 4) {
            const expanded = color
                .slice(1)
                .split('')
                .map((char) => `${char}${char}`)
                .join('');

            return `#${expanded}`.toLowerCase();
        }

        return color.toLowerCase();
    }

    normalizePositiveInt(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const normalized = Number.parseInt(String(value), 10);
        if (!Number.isFinite(normalized) || normalized <= 0) {
            return null;
        }

        return normalized;
    }

    shiftColor(hexColor, amount) {
        const hex = this.normalizeHexColor(hexColor);
        if (!hex) {
            return '#33a893';
        }

        const num = parseInt(hex.slice(1), 16);
        const clamp = (value) => Math.max(0, Math.min(255, value));
        const r = clamp((num >> 16) + amount);
        const g = clamp(((num >> 8) & 0x00ff) + amount);
        const b = clamp((num & 0x0000ff) + amount);

        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }

    contrastColor(hexColor) {
        const hex = this.normalizeHexColor(hexColor);
        if (!hex) {
            return '#0f1f22';
        }

        const num = parseInt(hex.slice(1), 16);
        const r = num >> 16;
        const g = (num >> 8) & 0x00ff;
        const b = num & 0x0000ff;
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        return brightness > 150 ? '#0f1f22' : '#ffffff';
    }

    resolveMediaUrl(payload) {
        if (!payload) {
            return null;
        }

        if (typeof payload.media_url === 'string' && payload.media_url.trim() !== '') {
            return payload.media_url.trim();
        }

        const media = payload.media;
        if (typeof media === 'string' && media.trim() !== '') {
            return media.trim();
        }

        if (media && typeof media === 'object') {
            const candidates = [media.url, media.path, media.src, media.value];
            const found = candidates.find((value) => typeof value === 'string' && value.trim() !== '');
            if (found) {
                return found.trim();
            }
        }

        return null;
    }

    resolvePackageMediaUrl(pkg) {
        const mediaUrl = this.resolveMediaUrl(pkg);
        const slug = String(pkg?.slug || '').toLowerCase().trim();
        const name = String(pkg?.name || '').toLowerCase().trim();
        const isPricePerformancePackage = slug === 'smart' || name.includes('preis-leistung');
        const hasWrongWayImage = typeof mediaUrl === 'string'
            && mediaUrl.includes('photo-1506702315536-dd8b83e2dcf9');

        if (isPricePerformancePackage && (!mediaUrl || hasWrongWayImage)) {
            return this.buildInlinePlaceholderMediaUrl();
        }

        return mediaUrl;
    }

    buildInlinePlaceholderMediaUrl() {
        const svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1400 800'><defs><linearGradient id='chatPvGradient' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='%23103138'/><stop offset='100%' stop-color='%23295f5a'/></linearGradient></defs><rect width='1400' height='800' fill='url(%23chatPvGradient)'/><g fill='none' stroke='%23ffffff' stroke-opacity='0.28' stroke-width='8'><path d='M120 560h1160'/><path d='M220 500h960'/><path d='M320 440h760'/></g><g fill='%23f6c65b'><rect x='150' y='120' width='78' height='78' rx='14'/><rect x='124' y='154' width='130' height='10' rx='5'/><rect x='184' y='94' width='10' height='130' rx='5'/></g><g fill='%23ffffff' fill-opacity='0.92'><rect x='390' y='170' width='220' height='120' rx='14'/><rect x='650' y='170' width='220' height='120' rx='14'/><rect x='910' y='170' width='220' height='120' rx='14'/><rect x='390' y='320' width='220' height='120' rx='14'/><rect x='650' y='320' width='220' height='120' rx='14'/><rect x='910' y='320' width='220' height='120' rx='14'/></g><text x='700' y='690' font-family='Figtree,Arial,sans-serif' font-size='56' text-anchor='middle' fill='%23ffffff'>Preis-Leistung Paket</text></svg>";
        const normalizedSvg = svg.replace(/%23/g, '#');

        return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(normalizedSvg)}`;
    }

    appendCardMedia(card, mediaUrl, altText) {
        if (!card || !mediaUrl) {
            return;
        }

        const mediaWrap = document.createElement('div');
        mediaWrap.className = 'chat-package-card__media';

        const img = document.createElement('img');
        img.src = mediaUrl;
        img.alt = altText || '';
        img.loading = 'lazy';
        img.decoding = 'async';

        mediaWrap.appendChild(img);
        card.appendChild(mediaWrap);
    }

    createCardCheck() {
        const marker = document.createElement('span');
        marker.className = 'chat-package-card__check';
        marker.innerHTML = `
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9.2 16.6L4.8 12.2l1.4-1.4 3 3 8.6-8.6 1.4 1.4z"></path>
            </svg>
        `;

        return marker;
    }

    formatCategoryLabel(category) {
        const normalized = String(category || '')
            .replace(/[_-]+/g, ' ')
            .trim();
        if (!normalized) {
            return '';
        }

        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }

    getSelectedPackageId() {
        const packageBlock = this.blocks.find((block) => block.type === 'package_select');
        if (!packageBlock) {
            return null;
        }

        const value = this.answers[packageBlock.id]?.value ?? null;
        return Number.isFinite(Number(value)) ? Number(value) : null;
    }

    isAddonAllowedByConstraints(addon, selectedPackageId, selectedSet) {
        const constraints = addon?.constraints || {};
        const requiredPackages = Array.isArray(constraints.requires_package_ids)
            ? constraints.requires_package_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
            : [];

        if (requiredPackages.length > 0) {
            if (!selectedPackageId || !requiredPackages.includes(selectedPackageId)) {
                return false;
            }
        }

        const excludes = Array.isArray(constraints.excludes_addon_ids)
            ? constraints.excludes_addon_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
            : [];
        const selectedIds = Array.from(selectedSet.values()).map((id) => Number(id));
        return excludes.every((id) => !selectedIds.includes(id));
    }

    computeLocalPricePreview() {
        const packageBlock = this.blocks.find((block) => block.type === 'package_select');
        if (!packageBlock) {
            return null;
        }

        const packageId = this.answers[packageBlock.id]?.value ?? null;
        if (!packageId) {
            return null;
        }

        const selectedPackage = (packageBlock.packages || []).find((pkg) => Number(pkg.id) === Number(packageId));
        if (!selectedPackage) {
            return null;
        }

        let totals = this.pricingToRange(selectedPackage.pricing);

        const addonBlocks = this.blocks.filter((block) => block.type === 'addon_select');
        addonBlocks.forEach((block) => {
            const addonIds = Array.isArray(this.answers[block.id]?.value) ? this.answers[block.id].value : [];
            addonIds.forEach((addonId) => {
                const addon = (block.addons || []).find((item) => Number(item.id) === Number(addonId));
                if (!addon) {
                    return;
                }
                const range = this.pricingToRange(addon.pricing, totals.currency);
                totals = {
                    currency: totals.currency || range.currency,
                    min: totals.min + range.min,
                    max: totals.max + range.max,
                };
            });
        });

        return totals;
    }

    pricingToRange(pricing, fallbackCurrency = 'EUR') {
        const data = pricing || {};
        const currency = data.currency || fallbackCurrency;
        if (data.type === 'range') {
            const min = Number.parseFloat(data.min ?? 0);
            const max = Number.parseFloat(data.max ?? min);
            const safeMin = Number.isFinite(min) ? min : 0;
            const safeMax = Number.isFinite(max) ? max : safeMin;
            return {
                currency,
                min: Math.min(safeMin, safeMax),
                max: Math.max(safeMin, safeMax),
            };
        }

        const amount = Number.parseFloat(data.amount ?? data.min ?? 0);
        const normalizedAmount = Number.isFinite(amount) ? amount : 0;
        return {
            currency,
            min: normalizedAmount,
            max: normalizedAmount,
        };
    }

    formatPricingRange(pricing) {
        const range = this.pricingToRange(pricing);
        return this.formatPricePreview(range);
    }

    formatPricePreview(range) {
        if (!range) {
            return '';
        }

        const formatter = (value) => Number(value || 0).toLocaleString('de-DE', {
            maximumFractionDigits: 0,
        });
        if (range.min === range.max) {
            return `${formatter(range.min)} ${range.currency}`;
        }

        return `${formatter(range.min)} – ${formatter(range.max)} ${range.currency}`;
    }
}
